import { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface TemplateMarkers {
  TAG_SECTION: string;
  SOLUTION_TYPE: string;
  SOLUTION_TYPE_GUIDELINES: string;
  EXAMPLE_SECTION: string;
  EXISTING_TAGS: string;
}

/**
 * Service for retrieving and processing AI prompt templates from database
 */
export class PromptTemplateService {
  private static cache = new Map<string, string>();

  /**
   * Get the complete system prompt with processed template markers
   * Uses database prompts with fallback to legacy
   */
  static async getSystemPrompt(solutionType: string, existingTags: string[] = []): Promise<string> {
    try {
      // Clear cache to ensure we get updated prompts
      this.clearCache();
      const template = await this.getPromptTemplate('ai_system_prompt_template');

      const markers: TemplateMarkers = {
        TAG_SECTION: await this.getTagSection(existingTags),
        SOLUTION_TYPE: solutionType,
        SOLUTION_TYPE_GUIDELINES: await this.getSolutionTypeGuidelines(solutionType),
        EXAMPLE_SECTION: await this.getPromptTemplate('ai_prompt_example_section'),
        EXISTING_TAGS: existingTags.join(', ')
      };

      return this.processTemplate(template, markers);
    } catch (error) {
      console.warn('Failed to fetch prompt from database:', error);

      // Legacy fallback disabled for now- will throw error if database access fails
      // return this.buildLegacyPrompt(solutionType, existingTags);
    }
  }

  /**
   * Retrieve a prompt template from database settings
   */
  private static async getPromptTemplate(key: string): Promise<string> {
    // Check cache first
    if (this.cache.has(key)) {
      return this.cache.get(key)!;
    }

    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', key)
      .maybeSingle();

    if (error || !data) {
      throw new Error(`Failed to retrieve prompt template: ${key}`);
    }

    // Cache the result
    this.cache.set(key, data.value);
    return data.value;
  }

  /**
   * Get the tag section with existing tags if available
   */
  private static async getTagSection(existingTags: string[]): Promise<string> {
    if (existingTags.length === 0) {
      return '';
    }

    const tagTemplate = await this.getPromptTemplate('ai_prompt_tag_section_template');
    return tagTemplate.replace('{{EXISTING_TAGS}}', existingTags.join(', '));
  }

  /**
   * Get solution type specific guidelines
   */
  private static async getSolutionTypeGuidelines(solutionType: string): Promise<string> {
    const guidelinesJson = await this.getPromptTemplate('ai_prompt_solution_type_guidelines');
    const guidelines = JSON.parse(guidelinesJson);

    return guidelines[solutionType] || guidelines['default'];
  }

  /**
   * Process template by replacing markers with actual values
   */
  private static processTemplate(template: string, markers: TemplateMarkers): string {
    let result = template;

    // Replace all template markers
    result = result.replace(/\{\{TAG_SECTION\}\}/g, markers.TAG_SECTION);
    result = result.replace(/\{\{SOLUTION_TYPE\}\}/g, markers.SOLUTION_TYPE);
    result = result.replace(/\{\{SOLUTION_TYPE_GUIDELINES\}\}/g, markers.SOLUTION_TYPE_GUIDELINES);
    result = result.replace(/\{\{EXAMPLE_SECTION\}\}/g, markers.EXAMPLE_SECTION);

    return result;
  }

  /**
   * Clear the cache (useful for testing or when settings change)
   */
  static clearCache(): void {
    this.cache.clear();
  }

}